import React, { useEffect, useMemo, useRef } from "react";

/**
 * WheelPicker
 * - Scrollable, snapping wheel
 * - Shows a few options above/below the selected value
 * - Centers the selected item; snaps on scroll end
 *
 * Props:
 *  - options: number[] | string[] (e.g., [1,2,3,4,5,6,7,8])
 *  - value: number|string
 *  - onChange: (val) => void
 *  - onClose: () => void   // called when clicking outside or pressing Escape
 *
 * Styling assumptions:
 *  - Parent should control button styling. This picker renders a small, absolute panel.
 */
export default function WheelPicker({ options = [], value, onChange, onClose }) {
  const containerRef = useRef(null);
  const itemHeight = 40;           // px — keep in sync with classes below
  const visibleCount = 5;          // odd number; item in the middle is "selected"
  const padCount = Math.floor(visibleCount / 2);

  const padded = useMemo(() => {
    // pad with empty slots to allow centering at edges
    return [
      ...Array(padCount).fill(null),
      ...options,
      ...Array(padCount).fill(null),
    ];
  }, [options, padCount]);

  // Scroll to initial value on open
  useEffect(() => {
    const idx = options.findIndex((o) => String(o) === String(value));
    if (!containerRef.current || idx < 0) return;
    const scrollTop = idx * itemHeight;
    containerRef.current.scrollTo({ top: scrollTop, behavior: "instant" });
  }, [options, value]);

  // Close on outside click / Esc
  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.parentElement?.contains(e.target)) {
        onClose?.();
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  // Snap logic on scroll end
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let t = null;
    const onScroll = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => {
        const rawIndex = Math.round(el.scrollTop / itemHeight);
        const clamped = Math.max(0, Math.min(rawIndex, options.length - 1));
        const targetTop = clamped * itemHeight;
        el.scrollTo({ top: targetTop, behavior: "smooth" });
        const nextVal = options[clamped];
        if (String(nextVal) !== String(value)) {
          onChange?.(nextVal);
        }
      }, 80);
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [options, value, onChange]);

  return (
    <div className="absolute z-50 mt-2 w-16 rounded-lg border bg-white shadow-lg">
      {/* highlight line for center */}
      <div className="relative">
        <div className="pointer-events-none absolute left-0 right-0 top-1/2 -translate-y-1/2 border-t" />
        <div
          ref={containerRef}
          className="max-h-[200px] overflow-y-auto scroll-smooth scrollbar-hidden"
          style={{
            // 5 items visible (5 * 40 = 200px)
            height: `${visibleCount * itemHeight}px`,
            scrollSnapType: "y mandatory",
          }}
          role="listbox"
          aria-label="Select beats"
        >
          {padded.map((opt, i) => {
            const isPad = opt === null;
            const isSelected = String(opt) === String(value);
            return (
              <div
                key={`${opt ?? "pad"}-${i}`}
                className={`h-10 flex items-center justify-center select-none ${
                  isPad ? "text-transparent" : isSelected ? "font-semibold" : "text-gray-700"
                }`}
                style={{ scrollSnapAlign: "center" }}
                role={isPad ? undefined : "option"}
                aria-selected={isSelected || undefined}
                onClick={() => {
                  if (isPad) return;
                  onChange?.(opt);
                }}
              >
                {opt ?? "—"}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
